// src/features/reabertura/components/ReaberturaPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./ReaberturaPage.module.css";

import { listarFinalizadas, listarWf3All, reabrirProgramacoes } from "../api";
import ReaberturaTable from "./ReaberturaTable";
import ReaberturaAuditList from "./ReaberturaAuditList";
import ReaberturaModal from "../popup/ReaberturaModal";

/* =========================
   Helpers (data)
   ========================= */
function pad2(n) {
  return String(n).padStart(2, "0");
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function firstOfMonth(dateStr) {
  const d = new Date(dateStr || todayISO());
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
}
function lastOfMonth(dateStr) {
  const d = new Date(dateStr || todayISO());
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${pad2(last.getMonth() + 1)}-${pad2(last.getDate())}`;
}
function monthRange(dateStr) {
  return { from: firstOfMonth(dateStr), to: lastOfMonth(dateStr) };
}

/* Código só por números */
function onlyDigits(v) {
  return String(v ?? "").replace(/\D+/g, "");
}

/* =========================
   Helpers (id/status/date)
   ========================= */
function getRowId(r) {
  const v = r?.id ?? r?.id_programacao ?? r?.programacao_id ?? null;
  return v == null ? "" : String(v).trim();
}
function normUp(v) {
  return (v == null ? "" : String(v)).trim().toUpperCase();
}

/** robusto: aceita 1, "1", true, "true" */
function isFinal(row) {
  const finFlag = row?.is_finalizado;
  if (finFlag === 1 || finFlag === true || finFlag === "1" || finFlag === "true") return true;
  return normUp(row?.status_wf3) === "FINALIZADO";
}

/** aceita ABERTO como ABERTA */
function statusKey(row) {
  if (!row) return "";
  if (isFinal(row)) return "FINALIZADO";
  const s = normUp(row?.status_wf3);
  if (s === "ABERTO") return "ABERTA";
  return s || "";
}

function dateKeyFromRow(r) {
  const v =
    r?.data ??
    r?.dia ??
    r?.data_execucao ??
    r?.data_programacao ??
    r?.dataExecucao ??
    r?.dataProgramacao ??
    null;

  if (!v) return "";
  const s = String(v).trim();
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return "";
}

/* =========================
   Sanitização de IDs
   ========================= */
function sanitizeIdsToNums(input) {
  const arr = Array.isArray(input) ? input : [input];
  const nums = [];

  for (const x of arr) {
    if (x == null) continue;

    if (typeof x === "number" && Number.isFinite(x)) {
      nums.push(Math.trunc(x));
      continue;
    }

    const s = String(x).trim();
    if (!s) continue;

    const parts = s.split(/[^\d]+/).filter(Boolean);
    for (const p of parts) nums.push(Number(p));
  }

  return Array.from(new Set(nums.filter((n) => Number.isFinite(n))));
}
function sanitizeIdsToStr(input) {
  return sanitizeIdsToNums(input).map(String);
}
function cryptoId() {
  return Math.random().toString(36).slice(2, 9);
}
function summarizeReopen(resp) {
  if (!resp) return "";
  const updated = Number(resp.updated || 0);
  const locked = Array.isArray(resp.skipped_locked) ? resp.skipped_locked.length : 0;
  const notFinal = Array.isArray(resp.skipped_not_final) ? resp.skipped_not_final.length : 0;
  const fails = Array.isArray(resp.fails) ? resp.fails.length : 0;

  const parts = [];
  parts.push(`reabertas: ${updated}`);
  if (locked) parts.push(`travadas(REPROGRAMADO): ${locked}`);
  if (notFinal) parts.push(`não-finalizadas: ${notFinal}`);
  if (fails) parts.push(`falhas: ${fails}`);
  return parts.join(" | ");
}

/* =========================
   Filtros
   ========================= */
const STATUS_OPTIONS = [
  { value: "TODOS", label: "Todos" },
  { value: "FINALIZADO", label: "Finalizado" },
  { value: "ABERTA", label: "Aberta" },
  { value: "REPROGRAMADO", label: "Reprogramado" },
];
const STATUS_ALLOWED = new Set(STATUS_OPTIONS.map((o) => o.value));

function readInitialFromSearchParams(sp) {
  const f = sp.get("from");
  const t = sp.get("to");

  const code = onlyDigits(sp.get("code") || "");
  let status = (sp.get("status") || "TODOS").trim().toUpperCase() || "TODOS";
  if (!STATUS_ALLOWED.has(status)) status = "TODOS";

  if (f && t) return { from: f, to: t, code, status };

  const mr = monthRange(todayISO());
  return { from: mr.from, to: mr.to, code, status };
}

/* =========================
   Status Dropdown (CUSTOM)
   ========================= */
function StatusDropdown({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const current = useMemo(() => {
    const v = String(value || "TODOS").toUpperCase();
    return options.find((o) => o.value === v) || options[0];
  }, [value, options]);

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  return (
    <div ref={wrapRef} className={`${styles.selectWrapCustom} ${open ? styles.selectWrapCustomOpen : ""}`}>
      <button
        type="button"
        className={styles.selectBtn}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((s) => !s);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {current?.label || "Todos"}
      </button>

      {open && (
        <div
          className={styles.dropdown}
          role="listbox"
          aria-label="Status"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((opt) => {
            const active = opt.value === current.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`${styles.option} ${active ? styles.optionActive : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(opt.value);
                  setOpen(false);
                }}
                role="option"
                aria-selected={active}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ReaberturaPage({ onBack }) {
  const [sp, setSp] = useSearchParams();

  // inicial só 1x
  const initialRef = useRef(null);
  if (!initialRef.current) initialRef.current = readInitialFromSearchParams(sp);
  const initial = initialRef.current;

  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);

  // codeInput = digitado / codeFilter = aplicado
  const [codeInput, setCodeInput] = useState(onlyDigits(initial.code || ""));
  const [codeFilter, setCodeFilter] = useState(onlyDigits(initial.code || ""));

  const [statusFilter, setStatusFilter] = useState(initial.status || "TODOS");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [audit, setAudit] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIds, setModalIds] = useState([]);
  const [reason, setReason] = useState("");

  const setFilters = useCallback(
    (patch) => {
      const next = new URLSearchParams(sp);

      const merged = {
        from: dateFrom,
        to: dateTo,
        code: codeFilter,
        status: statusFilter,
        ...patch,
      };

      if (merged.from) next.set("from", merged.from);
      else next.delete("from");

      if (merged.to) next.set("to", merged.to);
      else next.delete("to");

      const code = onlyDigits(merged.code || "");
      if (code) next.set("code", code);
      else next.delete("code");

      let st = String(merged.status || "TODOS").trim().toUpperCase();
      if (!STATUS_ALLOWED.has(st)) st = "TODOS";
      if (st && st !== "TODOS") next.set("status", st);
      else next.delete("status");

      setSp(next, { replace: true });
    },
    [sp, setSp, dateFrom, dateTo, codeFilter, statusFilter]
  );

  const refetch = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      const from = String(dateFrom || "");
      const to = String(dateTo || "");

      const code = onlyDigits(codeFilter || "");
      let st = (statusFilter || "TODOS").trim().toUpperCase();
      if (!STATUS_ALLOWED.has(st)) st = "TODOS";

      let items = [];

      if (st === "FINALIZADO") {
        items = await listarFinalizadas({ from, to, code, limit: 2000 });
      } else {
        items = await listarWf3All({ code, limit: 2000, offset: 0 });

        // período
        items = (items || []).filter((r) => {
          const d = dateKeyFromRow(r);
          if (!d) return true;
          return d >= from && d <= to;
        });

        // status (ABERTA / REPROGRAMADO)
        if (st && st !== "TODOS") {
          items = (items || []).filter((r) => statusKey(r) === st);
        }
      }

      // garante por dígitos no código
      if (code) {
        items = (items || []).filter((r) => onlyDigits(r?.codigo_unico).includes(code));
      }

      setRows(Array.isArray(items) ? items : []);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Falha ao carregar dados.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, codeFilter, statusFilter]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const applyCodeNow = useCallback(
    (raw) => {
      const v = onlyDigits(raw || "");
      const current = onlyDigits(codeFilter || "");
      if (v === current) return;

      setCodeFilter(v);
      setFilters({ code: v });
    },
    [codeFilter, setFilters]
  );

  // debounce enquanto digita
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => applyCodeNow(codeInput), 350);
    return () => clearTimeout(debounceRef.current);
  }, [codeInput, applyCodeNow]);

  const handleFromChange = useCallback(
    (v) => {
      const { from, to } = monthRange(v);
      setDateFrom(from);
      setDateTo(to);
      setFilters({ from, to });
    },
    [setFilters]
  );

  const handleToChange = useCallback(
    (v) => {
      const { from, to } = monthRange(v);
      setDateFrom(from);
      setDateTo(to);
      setFilters({ from, to });
    },
    [setFilters]
  );

  const visibleRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  // ✅ mapa id -> row (para validar FINALIZADO antes de abrir modal)
  const rowById = useMemo(() => {
    const m = new Map();
    for (const r of visibleRows) {
      const id = getRowId(r);
      if (id) m.set(id, r);
    }
    return m;
  }, [visibleRows]);

  // minis: total / finalizados / abertas / reprogramados
  const minis = useMemo(() => {
    let total = visibleRows.length;
    let fin = 0;
    let aberta = 0;
    let reprog = 0;

    for (const r of visibleRows) {
      const st = statusKey(r);
      if (st === "FINALIZADO") fin++;
      else if (st === "ABERTA") aberta++;
      else if (st === "REPROGRAMADO") reprog++;
    }

    return { total, fin, aberta, reprog };
  }, [visibleRows]);

  // ✅ Só abre modal se o(s) id(s) forem FINALIZADO
  const openModalFor = useCallback(
    (idsOrId) => {
      const cleanIds = sanitizeIdsToStr(idsOrId);
      if (!cleanIds.length) return;

      const finalsOnly = cleanIds.filter((id) => {
        const row = rowById.get(String(id));
        return row && isFinal(row);
      });

      if (!finalsOnly.length) return; // não abre popup para ABERTA/REPROGRAMADO

      setModalIds(finalsOnly);
      setReason("");
      setModalOpen(true);
    },
    [rowById]
  );

  const handleConfirmReopen = useCallback(async () => {
    if (!reason.trim()) {
      alert("Informe o motivo da reabertura.");
      return;
    }

    const idsNum = sanitizeIdsToNums(modalIds);
    if (!idsNum.length) {
      alert("ids inválidos (nenhum ID numérico encontrado).");
      return;
    }

    try {
      const now = new Date().toISOString();

      const resp = await reabrirProgramacoes({
        ids: idsNum,
        reason: reason.trim(),
      });

      if (!resp?.ok) {
        alert(resp?.error || "Falha ao reabrir.");
        return;
      }

      const idsStr = idsNum.map(String);

      setRows((prev) =>
        (Array.isArray(prev) ? prev : []).map((r) => {
          const id = getRowId(r);
          if (!id || !idsStr.includes(id)) return r;
          return { ...r, status_wf3: "ABERTA", is_finalizado: 0, atualizado_em: now };
        })
      );

      const summary = summarizeReopen(resp);
      setAudit((prev) => [
        { id: cryptoId(), when: now, ids: idsStr, reason: reason.trim(), actor: "Você", summary },
        ...prev,
      ]);

      setModalOpen(false);

      alert(`OK — ${summary}`);
      refetch();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.error || e?.message || "Falha ao reabrir.";
      alert(msg);
    }
  }, [reason, modalIds, refetch]);

  return (
    <div className={styles.root}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarInner}>
          <button className={styles.btn} onClick={onBack}>
            Voltar
          </button>

          <div className={styles.centerStack}>
            <div className={styles.block}>
              <div className={styles.titleSmall}>Período</div>
              <div className={styles.rowDates}>
                <input
                  type="date"
                  className={`${styles.inputLike} ${styles.half}`}
                  value={dateFrom}
                  onChange={(e) => handleFromChange(e.target.value)}
                  max={lastOfMonth(dateFrom)}
                />
                <input
                  type="date"
                  className={`${styles.inputLike} ${styles.half}`}
                  value={dateTo}
                  onChange={(e) => handleToChange(e.target.value)}
                  min={firstOfMonth(dateTo)}
                />
              </div>
            </div>

            <div className={styles.block}>
              <div className={styles.titleSmall}>Código</div>
              <input
                type="text"
                inputMode="numeric"
                placeholder="Somente números"
                className={`${styles.inputLike} ${styles.codeInput}`}
                value={codeInput}
                onChange={(e) => setCodeInput(onlyDigits(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyCodeNow(codeInput);
                }}
                onBlur={() => applyCodeNow(codeInput)}
              />
            </div>

            <div className={styles.block}>
              <div className={styles.titleSmall}>Status</div>
              <StatusDropdown
                value={statusFilter}
                options={STATUS_OPTIONS}
                onChange={(v) => {
                  const up = String(v || "TODOS").toUpperCase();
                  setStatusFilter(up);
                  setFilters({ status: up });
                }}
              />
            </div>
          </div>

          {/* Minis (4 cards) */}
          <div className={styles.sidebarBottom}>
            <div className={styles.miniRow}>
              <div className={styles.mini}>
                <div className={styles.miniLbl}>Total</div>
                <div className={styles.miniVal}>{minis.total}</div>
              </div>

              <div className={styles.mini}>
                <div className={styles.miniLbl}>Finalizados</div>
                <div className={styles.miniVal}>{minis.fin}</div>
              </div>

              <div className={styles.mini}>
                <div className={styles.miniLbl}>Abertas</div>
                <div className={styles.miniVal}>{minis.aberta}</div>
              </div>

              <div className={styles.mini}>
                <div className={styles.miniLbl}>Reprogramados</div>
                <div className={styles.miniVal}>{minis.reprog}</div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <div className={styles.content}>
        <div className={styles.contentHeader}>
          <h2 className={styles.title}>Controle e Reabertura</h2>
        </div>

        <section className={styles.sectionBox}>
          {loading && rows.length === 0 ? (
            <div className={styles.centerMsg}>Carregando…</div>
          ) : err ? (
            <div className={styles.centerMsg}>{err}</div>
          ) : visibleRows.length === 0 ? (
            <div className={styles.centerMsg}>Nenhum item encontrado (verifique período/código/status).</div>
          ) : (
            <ReaberturaTable rows={visibleRows} onReopen={(id) => openModalFor(id)} />
          )}
        </section>

        <section className={styles.auditSection}>
          <ReaberturaAuditList audit={audit} />
        </section>
      </div>

      <ReaberturaModal
        open={modalOpen}
        ids={modalIds}
        reason={reason}
        setReason={setReason}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmReopen}
      />
    </div>
  );
}
